import React, { useState } from 'react';
import { Badge, Button, Card, Drawer, EmptyState, Input, Select, Spinner, Switch, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { settingsApi, CURRENCY_OPTIONS, TIMEZONE_OPTIONS } from './settingsApi';

const EMPTY = { name: '', baseCurrency: 'USD', timezone: 'UTC', prostayPropertyId: '', isActive: true };

function toForm(p) {
  if (!p) return { ...EMPTY };
  return {
    name: p.name ?? '',
    baseCurrency: p.baseCurrency ?? 'USD',
    timezone: p.timezone ?? 'UTC',
    prostayPropertyId: p.prostayPropertyId ?? '',
    isActive: p.isActive ?? true,
  };
}

function PropertyForm({ initial, submitting, onSubmit, onCancel }) {
  const t = useT();
  const isEdit = !!initial;
  const [form, setForm] = useState(() => toForm(initial));
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = t('Name is required.');
    setErrors(next);
    if (Object.keys(next).length) return;
    onSubmit({
      name: form.name.trim(),
      baseCurrency: form.baseCurrency,
      timezone: form.timezone,
      prostayPropertyId: form.prostayPropertyId.trim() || null,
      isActive: form.isActive,
    });
  };

  return (
    <form className="set-form" onSubmit={submit}>
      <Input label={t('Property name')} value={form.name} onChange={set('name')} error={errors.name} placeholder="Komodo Resort" autoFocus />
      <div className="ps-field">
        <span className="sp-field-label">{t('Base currency')}</span>
        <Select value={form.baseCurrency} onChange={setVal('baseCurrency')} options={CURRENCY_OPTIONS} ariaLabel={t('Base currency')} />
      </div>
      <div className="ps-field">
        <span className="sp-field-label">{t('Timezone')}</span>
        <Select value={form.timezone} onChange={setVal('timezone')} options={TIMEZONE_OPTIONS} ariaLabel={t('Timezone')} />
      </div>
      <Input
        label={t('Prostay / TableView property ID')}
        value={form.prostayPropertyId}
        onChange={set('prostayPropertyId')}
        hint={t('Optional — leave blank for a standalone property with no PMS integration.')}
      />
      <Switch checked={form.isActive} onChange={setVal('isActive')} label={t('Active')} />
      <div className="set-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>{t('Cancel')}</Button>
        <Button type="submit" variant="primary" loading={submitting}>{isEdit ? t('Save changes') : t('Create property')}</Button>
      </div>
    </form>
  );
}

export default function PropertiesTab({ properties, loading, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (submitting) return;
    setDrawerOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing) {
        const updated = await settingsApi.updateProperty(editing.id, { id: editing.id, ...payload });
        toast.success(t('Property updated'));
        setDrawerOpen(false);
        setEditing(null);
        onChanged?.(updated);
      } else {
        const created = await settingsApi.createProperty(payload);
        toast.success(t('Property created'));
        setDrawerOpen(false);
        onChanged?.(created);
      }
    } catch (err) {
      toast.error(editing ? t('Could not update property') : t('Could not create property'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="set-form">
      <div className="set-section__head">
        <div>
          <h3 className="set-section__title">{t('Properties')}</h3>
          <p className="set-section__hint">{t('Hotels, resorts and venues in this organization. Not every property needs a Prostay or TableView integration.')}</p>
        </div>
        <Button variant="primary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add property')}</Button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
      ) : properties.length === 0 ? (
        <EmptyState
          icon={<Icon.Building size={28} />}
          title={t('No properties yet')}
          description={t('Create your first property to enable per-property settings and scoping.')}
          action={<Button variant="primary" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add property')}</Button>}
        />
      ) : (
        <div className="set-prop-list">
          {properties.map((p) => (
            <Card key={p.id} variant="flat">
              <Card.Body>
                <div className="set-prop">
                  <div className="set-prop__main">
                    <span className="set-prop__name">
                      {p.name}
                      {!p.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
                      {p.prostayPropertyId
                        ? <Badge tone="brand" dot>{t('Linked')}</Badge>
                        : <Badge tone="neutral">{t('Standalone')}</Badge>}
                    </span>
                    <span className="set-prop__meta">
                      <span><Icon.Coins size={13} /> {p.baseCurrency}</span>
                      <span><Icon.Clock size={13} /> {p.timezone}</span>
                      {p.prostayPropertyId && <span>· {p.prostayPropertyId}</span>}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={<Icon.Pencil size={15} />}
                    onClick={() => openEdit(p)}
                  >
                    {t('Edit')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <Drawer open={drawerOpen} onClose={closeDrawer} title={editing ? t('Edit property') : t('Add property')} size="md">
        <PropertyForm
          key={editing?.id || 'new'}
          initial={editing}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={closeDrawer}
        />
      </Drawer>
    </div>
  );
}
